import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { liquidGlass, type LiquidGlassVariant } from "@/components/ui/LiquidGlass";
import { isInternalPath } from "@/lib/internalLink";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export function Container({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "mx-auto w-full max-w-[var(--layout-max)] px-5 sm:px-7 lg:px-9",
        className
      )}
    >
      {children}
    </div>
  );
}

export function SectionShell({
  children,
  className,
  id,
  variant = "default",
}: {
  children: ReactNode;
  className?: string;
  id?: string;
  variant?: "default" | "muted" | "contrast" | "elevated";
}) {
  const bg =
    variant === "muted"
      ? "bg-[var(--section-muted)]"
      : variant === "contrast"
        ? "bg-[var(--section-contrast)]"
        : variant === "elevated"
          ? "bg-[var(--bg2)]"
          : "bg-[var(--bg)]";

  return (
    <section
      id={id}
      className={cn(
        "relative overflow-hidden py-20 sm:py-24 lg:py-28",
        bg,
        variant === "contrast" && "section-contrast",
        className
      )}
    >
      {children}
    </section>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  description,
  align = "center",
  className,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  align?: "center" | "left";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "fade-up mb-14 max-w-3xl sm:mb-16",
        align === "center" && "mx-auto text-center",
        className
      )}
    >
      {eyebrow ? <p className="linear-label mb-4">{eyebrow}</p> : null}
      <h2 className="linear-section-title text-balance">{title}</h2>
      {description ? (
        <p className="linear-body mt-4">{description}</p>
      ) : null}
    </div>
  );
}

export function Chip({
  children,
  className,
  pulse,
}: {
  children: ReactNode;
  className?: string;
  pulse?: boolean;
}) {
  return (
    <span
      className={cn(
        liquidGlass("pill", "inline-flex items-center gap-2 px-3 py-1 text-xs font-medium text-[var(--text-sm)]"),
        className
      )}
    >
      {pulse ? (
        <span className="relative flex size-2">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-enabase-blue opacity-40" />
          <span className="relative inline-flex size-2 rounded-full bg-enabase-blue" />
        </span>
      ) : (
        <span className="size-1.5 rounded-full bg-[var(--text-sm)]" />
      )}
      {children}
    </span>
  );
}

export function GlassCard({
  children,
  className,
  hover,
  variant = "default",
}: {
  children: ReactNode;
  className?: string;
  hover?: boolean;
  variant?: LiquidGlassVariant;
}) {
  return (
    <div
      className={cn(
        liquidGlass(variant, "rounded-xl"),
        hover && "transition duration-200 hover:border-[var(--glass-border)] hover:shadow-lg",
        className
      )}
    >
      {children}
    </div>
  );
}

type LinkButtonProps = {
  href: string;
  children: ReactNode;
  className?: string;
  external?: boolean;
  size?: "default" | "sm" | "lg";
};

export function PrimaryButton({
  href,
  children,
  className,
  external,
  size = "lg",
}: LinkButtonProps) {
  const internal = !external && isInternalPath(href);

  return (
    <Button asChild variant="default" size={size} className={cn("no-underline", className)}>
      {internal ? (
        <Link to={href}>{children}</Link>
      ) : (
        <a
          href={href}
          target={external ? "_blank" : undefined}
          rel={external ? "noopener noreferrer" : undefined}
        >
          {children}
        </a>
      )}
    </Button>
  );
}

export function SecondaryButton({
  href,
  children,
  className,
  external,
  size = "lg",
}: {
  href?: string;
  children: ReactNode;
  className?: string;
  external?: boolean;
  size?: "default" | "sm" | "lg";
}) {
  if (href) {
    const internal = !external && isInternalPath(href);

    return (
      <Button asChild variant="outline" size={size} className={cn("no-underline", className)}>
        {internal ? (
          <Link to={href}>{children}</Link>
        ) : (
          <a
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
          >
            {children}
          </a>
        )}
      </Button>
    );
  }

  return (
    <Button type="button" variant="outline" size={size} className={className}>
      {children}
    </Button>
  );
}

export function GradientOrb({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute rounded-full blur-3xl",
        className
      )}
    />
  );
}
